import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { fetchGet } from '../../../../helpers/fetching';
import {
  setActionToConfirm,
  setSnackbar,
  setActiveDialog,
  sendStructuredReport,
  sendQualityReport,
  sendDensityReport,
  sendDiagnosticsReport,
  sendScoreCardReport,
  sendFHIRReport,
  sendHL7Report,
} from '../../../../store';
import { useTranslation } from 'react-i18next';
import animation from '../../../../resources/processing_animation.gif';
import { Divider, Tab, Tabs } from '@material-ui/core';

const Report = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const objectUrlsRef = useRef(new Set());

  const {
    confirmed_density,
    confirmed_quality,
    confirmed_opacities,
    confirmed_microcalc,
    pat_name,
    report_sending_rules,
    hl7_sending_rules,
    fhir_sending_rules,
  } = useSelector((state) => state.results);
  const currentExam = useSelector((state) => state.examinations.currentExam);
  const { premium_features } = useSelector((state) => state.global_settings);
  const landscape = useSelector((state) => state.gui.landscape);

  const [qualityPdfSrc, setQualityPdfSrc] = useState(null);
  const [densitySrc, setDensitySrc] = useState(null);
  const [diagnosticsPages, setDiagnosticsPages] = useState([]);
  const [diagnosticsIsPdf, setDiagnosticsIsPdf] = useState(true);
  const [scoreCardSrc, setScoreCardSrc] = useState(null);
  const [reportViewTab, setReportViewTab] = useState('score_card');
  const [loadingReport, setLoadingReport] = useState(null);

  const loggedUser = localStorage.getItem('username');
  const { diagnostics_microcalc_module, diagnostics_opacities_module, density_module, quality_module } = premium_features;

  const {
    quality_report_manual,
    diagnostics_report_manual,
    structured_report_manual,
    score_card_manual,
    density_report_manual,
    diagnostics_report_direct,
    quality_report_direct,
    structured_report_direct,
    score_card_direct,
    density_report_direct,
  } = report_sending_rules || {};
  const { fhir_manual, fhir_direct } = fhir_sending_rules || {};
  const { hl7_manual, hl7_direct } = hl7_sending_rules || {};

  const noRuleDefined = [
    quality_report_manual,
    diagnostics_report_manual,
    structured_report_manual,
    score_card_manual,
    diagnostics_report_direct,
    quality_report_direct,
    structured_report_direct,
    score_card_direct,
    density_report_manual,
    density_report_direct,
    fhir_manual,
    fhir_direct,
    hl7_manual,
    hl7_direct,
  ].every((value) => value == null || value === 0);

  const somePremiumDiagnostics =
    (diagnostics_microcalc_module || diagnostics_opacities_module) &&
    localStorage.getItem('diagnostics') === 'true';
  const densityReportEnabled = !!density_module &&
    (density_report_manual === 1 || density_report_direct === 1);
  const showScoreCard = noRuleDefined || score_card_manual === 1 || score_card_direct === 1;
  const qualityReportEnabled = !!quality_module &&
    (quality_report_manual === 1 || quality_report_direct === 1);
  const showQuality = qualityReportEnabled || (noRuleDefined && quality_module);
  const showDensity = densityReportEnabled || (noRuleDefined && density_module);
  const showDiagnostics = !!somePremiumDiagnostics &&
    (noRuleDefined || diagnostics_report_manual === 1 || diagnostics_report_direct === 1);
  const reportTabs = [
    showScoreCard && { value: 'score_card', label: 'Score Card' },
    showQuality && { value: 'quality', label: 'Quality' },
    showDensity && { value: 'density', label: t('Settings.density') },
    showDiagnostics && { value: 'diagnostics', label: 'Diagnostics' },
  ].filter(Boolean);
  const activeReportTab = reportTabs.some(({ value }) => value === reportViewTab)
    ? reportViewTab
    : reportTabs[0]?.value || false;

  useEffect(() => {
    setReportViewTab(activeReportTab);
  }, [activeReportTab]);

  const revokeDiagnosticsPages = useCallback((pages) => {
    pages.forEach(({ url }) => {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
      }
    });
  }, []);

  const fetchReportBlob = async (route, abortSignal) => {
    const response = await fetchGet(route, 'bbox', false, 'blob', abortSignal);
    if (response?.aborted) {
      return { aborted: true };
    }
    const responseType = response?.contentType || response?.blob?.type || '';
    const isValid =
      response?.ok &&
      response?.blob &&
      ['image/', 'application/pdf'].some((prefix) => responseType.startsWith(prefix));
    return { response, responseType, isValid };
  };

  const fetchReports = useCallback(
    async (abortSignal) => {
      setLoadingReport(t('report.generating'));
      let hasError = false;

      try {
        const loadSingleReport = async (type, setter) => {
          const query = new URLSearchParams({ report_type: type });
          try {
            const { aborted, isValid, response, responseType } = await fetchReportBlob(
              `report/${currentExam}?${query.toString()}`,
              abortSignal,
            );
            if (aborted) {
              return 'aborted';
            }
            if (isValid) {
              const nextUrl = URL.createObjectURL(response.blob);
              objectUrlsRef.current.add(nextUrl);
              setter((prev) => {
                if (prev?.startsWith('blob:')) {
                  URL.revokeObjectURL(prev);
                  objectUrlsRef.current.delete(prev);
                }
                return nextUrl;
              });
              return 'ok';
            }
            console.warn('Report fetch failed or invalid content type', {
              reportType: type,
              status: response?.status,
              contentType: responseType,
            });
          } catch (error) {
            if (error?.name === 'AbortError') {
              return 'aborted';
            }
            console.warn('Report fetch crashed', { reportType: type, error });
          }
          setter((prev) => {
            if (prev?.startsWith('blob:')) {
              URL.revokeObjectURL(prev);
              objectUrlsRef.current.delete(prev);
            }
            return null;
          });
          return 'error';
        };

        for (const [enabled, type, setter] of [
          [showScoreCard, 'score_card', setScoreCardSrc],
          [showQuality, 'quality', setQualityPdfSrc],
          [showDensity, 'density', setDensitySrc],
        ]) {
          if (!enabled) {
            setter((prev) => {
              if (prev?.startsWith('blob:')) {
                URL.revokeObjectURL(prev);
                objectUrlsRef.current.delete(prev);
              }
              return null;
            });
            continue;
          }
          const status = await loadSingleReport(type, setter);
          if (status === 'aborted') {
            return;
          }
          if (status === 'error') {
            hasError = true;
          }
        }

        if (showDiagnostics) {
          try {
            const manifestQuery = new URLSearchParams({
              report_type: 'diagnostics',
              preview_manifest: '1',
            });
            const manifest = await fetchGet(
              `report/${currentExam}?${manifestQuery.toString()}`,
              'bbox',
              false,
              'json',
              abortSignal,
            );
            if (manifest?.aborted) {
              return;
            }

            const pages = manifest?.pages;
            if (!Array.isArray(pages) || pages.length === 0) {
              console.warn('Diagnostics manifest invalid', { manifest });
              setDiagnosticsPages((prev) => {
                revokeDiagnosticsPages(prev);
                return [];
              });
              hasError = true;
            } else {
              setDiagnosticsIsPdf(manifest.format === 'pdf');
              const loadedPages = [];

              for (const { label, page } of pages) {
                const pageQuery = new URLSearchParams({
                  report_type: 'diagnostics',
                  preview_page: page,
                });
                const { aborted, isValid, response } = await fetchReportBlob(
                  `report/${currentExam}?${pageQuery.toString()}`,
                  abortSignal,
                );
                if (aborted) {
                  return;
                }
                if (!isValid) {
                  hasError = true;
                  continue;
                }
                const url = URL.createObjectURL(response.blob);
                objectUrlsRef.current.add(url);
                loadedPages.push({ label, url });
              }

              setDiagnosticsPages((prev) => {
                revokeDiagnosticsPages(prev);
                return loadedPages;
              });
              if (!loadedPages.length) {
                hasError = true;
              }
            }
          } catch (error) {
            if (error?.name === 'AbortError') {
              return;
            }
            setDiagnosticsPages((prev) => {
              revokeDiagnosticsPages(prev);
              return [];
            });
            hasError = true;
          }
        } else {
          setDiagnosticsPages((prev) => {
            revokeDiagnosticsPages(prev);
            return [];
          });
        }
      } finally {
        setLoadingReport(null);
        if (hasError) {
          dispatch(
            setSnackbar({
              msg: 'report.failed_code',
              severity: 'warning',
            }),
          );
        }
      }
    },
    [
      currentExam, dispatch, revokeDiagnosticsPages,
      showScoreCard, showQuality, showDensity, showDiagnostics, t,
    ],
  );

  const onScoreCardSend = () => {
    if (
      ![confirmed_density, !quality_module || confirmed_quality, confirmed_opacities, confirmed_microcalc].every((item) => item)
    )
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendScoreCardReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.sending_score_card'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendScoreCardReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.sending_score_card'),
          severity: 'info',
        }),
      );
    }
  };

  const onSRSend = () => {
    if (![confirmed_density, !quality_module || confirmed_quality, confirmed_opacities].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendStructuredReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_structure_pacs'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendStructuredReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_structure_pacs'),
          severity: 'info',
        }),
      );
    }
  };

  const onQualityReportSend = () => {
    if (![confirmed_density, confirmed_quality].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendQualityReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_img'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendQualityReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_img'),
          severity: 'info',
        }),
      );
    }
  };

  const onDensityReportSend = () => {
    const send = () => {
      dispatch(sendDensityReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_img'),
          severity: 'info',
        }),
      );
    };
    if (!confirmed_density) {
      dispatch(
        setActionToConfirm({
          action: send,
          text: t('report.not_confirmed_send'),
        }),
      );
    } else {
      send();
    }
  };

  const onDiagnoseReportSend = () => {
    if (![confirmed_density, confirmed_opacities, confirmed_microcalc].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendDiagnosticsReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_img'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendDiagnosticsReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_img'),
          severity: 'info',
        }),
      );
    }
  };

  const onFHIRSend = () => {
    dispatch(sendFHIRReport(currentExam, loggedUser, t));
    dispatch(
      setSnackbar({
        msg: t('report.send_fhir'),
        severity: 'info',
      }),
    );
  };

  const onHL7Send = () => {
    dispatch(sendHL7Report(currentExam, loggedUser, t));
    dispatch(
      setSnackbar({
        msg: t('report.send_hl7'),
        severity: 'info',
      }),
    );
  };

  useEffect(() => {
    const abortReports = new AbortController();
    fetchReports(abortReports.signal);
    return () => abortReports.abort();
  }, [fetchReports]);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  return (
    <DraggableDialog
      maxWidth='xl'
      title={t('ResultView.report_title', { user: pat_name })}
      open={props.open}
      customStyle={{ overflow: 'auto' }}
      actions={[
        !landscape && {
          onClick: () => dispatch(setActiveDialog(null)),
          color: 'secondary',
          label: t('Console.close'),
        },
        somePremiumDiagnostics &&
          (diagnostics_report_manual === 1 || diagnostics_report_direct === 1) && {
            onClick: onDiagnoseReportSend,
            color: 'primary',
            label: t('report.send_diagnose'),
            disabled: !!loadingReport,
          },
        qualityReportEnabled && {
          onClick: onQualityReportSend,
          color: 'primary',
          label: t('report.send_quality'),
          disabled: !!loadingReport,
        },
        densityReportEnabled && {
          onClick: onDensityReportSend,
          color: 'primary',
          label: t('report.send_density'),
          disabled: !!loadingReport,
        },
        (structured_report_manual === 1 || structured_report_direct === 1) && {
          onClick: onSRSend,
          color: 'primary',
          label: t('report.send_sr'),
          disabled: !!loadingReport,
        },
        (score_card_manual === 1 || score_card_direct === 1) && {
          onClick: onScoreCardSend,
          color: 'primary',
          label: t('report.send_score_card'),
          disabled: !!loadingReport,
        },
        (fhir_manual === 1 || fhir_direct === 1) && {
          onClick: onFHIRSend,
          color: 'primary',
          label: t('report.send_fhir'),
          disabled: !!loadingReport,
        },
        (hl7_manual === 1 || hl7_direct === 1) && {
          onClick: onHL7Send,
          color: 'primary',
          label: t('report.send_hl7'),
          disabled: !!loadingReport,
        },
        noRuleDefined && {
          onClick: () => dispatch(setActiveDialog('config')),
          color: 'primary',
          label: t('report.set_rules'),
          disabled: !!loadingReport,
        },
      ]}
      onClose={() => dispatch(setActiveDialog(null))}>
      {loadingReport ? (
        <>
          <img
            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            src={animation}
            alt='logoanimation'
          />
          <p>{loadingReport}</p>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          {reportTabs.length === 0 && <p>{t('report.no_image_previews')}</p>}
          {reportTabs.length > 0 && (
            <Tabs
              value={activeReportTab}
              onChange={(_, tabValue) => setReportViewTab(tabValue)}
              indicatorColor='primary'
              textColor='primary'
              centered>
              {reportTabs.map(({ value, label }) => (
                <Tab key={value} value={value} label={label} />
              ))}
            </Tabs>
          )}

          {reportTabs.length > 0 && (
            <Divider orientation='horizontal' variant='fullWidth' style={{ margin: '10px 0' }} />
          )}

          {activeReportTab === 'score_card' &&
            (scoreCardSrc ? (
              <img
                src={scoreCardSrc}
                alt={t('report.score_card_unavailable')}
                style={{ maxWidth: '90vw', maxHeight: '70vh', objectFit: 'contain' }}
              />
            ) : (
              <p>{t('report.score_card_unavailable')}</p>
            ))}

          {activeReportTab === 'quality' &&
            (qualityPdfSrc ? (
              <iframe
                src={qualityPdfSrc}
                title='quality-report-pdf'
                style={{ width: '90vw', height: '80vh', border: 'none' }}
              />
            ) : (
              <p>{t('report.quality_unavailable')}</p>
            ))}

          {activeReportTab === 'density' &&
            (densitySrc ? (
              <iframe
                src={densitySrc}
                title='density-report'
                style={{ width: '90vw', height: '80vh', border: 'none' }}
              />
            ) : (
              <p>{t('report.density_unavailable')}</p>
            ))}

          {activeReportTab === 'diagnostics' &&
            (diagnosticsPages.length ? (
              <div style={{ maxHeight: '80vh', overflowY: 'auto', width: '90vw' }}>
                {diagnosticsPages.map(({ label, url }) =>
                  diagnosticsIsPdf ? (
                    <iframe
                      key={label}
                      src={url}
                      title={`diagnostics-${label}`}
                      style={{
                        width: '100%',
                        height: '80vh',
                        border: 'none',
                        marginBottom: 16,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <img
                      key={label}
                      src={url}
                      alt={label}
                      style={{
                        width: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        marginBottom: 16,
                        display: 'block',
                      }}
                    />
                  ),
                )}
              </div>
            ) : (
              <p>{t('report.diagnostics_unavailable')}</p>
            ))}
        </div>
      )}
    </DraggableDialog>
  );
};

export default Report;
